import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { getPack } from "@zeeta/shared";
import { CheckCircle2, Circle, Package, ShoppingCart, Truck } from "lucide-react";
import { ErrorMessage } from "@/components/error-message";
import { Stat } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { WeeklySummaryCard } from "@/features/ai/weekly-card";
import { meQuery } from "@/features/auth/queries";
import { type MessageKey, useI18n } from "@/i18n";
import { api } from "@/lib/api";
import { dhakaDayRange } from "@/lib/numbers";
import { useTenant } from "./context";
import { overviewQuery, type TenantOverview } from "./queries";

const STEPS = [
  "products",
  "opening_stock",
  "opening_dues",
  "printer",
  "staff",
  "payment_methods",
  "first_sale",
] as const;

interface Dashboard {
  salesCount: number;
  salesTotal: number | null;
  grossProfit: number | null;
  cashInHand: number | null;
  receivable: number | null;
  payable: number | null;
  lowStockCount: number;
  nearExpiryCount: number | null;
  expiredLotCount: number | null;
  trend: { day: string; total: number }[];
}

/** 30-day sales bars; days without sales show as gaps so the rhythm of trade is visible. */
function Trend({ data }: { data: Dashboard["trend"] }) {
  const { money, num } = useI18n();
  const days = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(Date.now() + 6 * 3_600_000 - (29 - i) * 86_400_000)
      .toISOString()
      .slice(0, 10);
    return { day: d, total: data.find((x) => x.day === d)?.total ?? 0 };
  });
  const max = Math.max(...days.map((d) => d.total), 1);
  return (
    <div
      className="flex h-32 items-end gap-[3px]"
      role="img"
      aria-label={days.map((d) => `${d.day}: ${money(d.total)}`).join(", ")}
    >
      {days.map((d) => (
        <div key={d.day} className="group relative flex-1">
          <div
            className="w-full rounded-sm bg-primary/80 group-hover:bg-primary"
            style={{
              height: `${Math.max((d.total / max) * 128, d.total > 0 ? 3 : 1)}px`,
              opacity: d.total > 0 ? 1 : 0.15,
            }}
          />
          <span className="pointer-events-none absolute bottom-full left-1/2 mb-1 hidden -translate-x-1/2 rounded bg-foreground px-1.5 py-0.5 text-[10px] whitespace-nowrap text-background group-hover:block">
            {num(d.day.slice(5))} · {money(d.total)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function DashboardPage() {
  const { tenantId, branch, settings, can, role } = useTenant();
  const { t, locale, money, num } = useI18n();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const today = dhakaDayRange(0, 1);
  const stats = useQuery({
    queryKey: ["tenant", tenantId, "dashboard", branch.id, today.from],
    queryFn: () =>
      api.get<Dashboard>(
        `/t/${tenantId}/dashboard?branchId=${branch.id}&from=${today.from}&to=${today.to}`,
      ),
    refetchInterval: 60_000,
  });
  const update = useMutation({
    mutationFn: (patch: { complete?: string[]; dismissed?: boolean }) =>
      api.patch<Omit<TenantOverview, "membership">>(`/t/${tenantId}/onboarding`, patch),
    onSuccess: (next) => {
      queryClient.setQueryData(overviewQuery(tenantId).queryKey, (prev) =>
        prev ? { ...prev, ...next } : prev,
      );
    },
  });

  const { completedSteps, dismissed } = settings.onboarding;
  const pack = getPack(settings.packId);
  const d = stats.data;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {me?.user.name
              ? t("dashboard.welcome", { name: me.user.name })
              : t("dashboard.welcomeNoName")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {settings.businessName}
            {pack ? ` · ${pack.name[locale]}` : ""} · {branch.name} ·{" "}
            {t(`roles.${role}` as MessageKey)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("sale.create") && (
            <Link to="/t/$tenantId/pos" params={{ tenantId }}>
              <Button>
                <ShoppingCart aria-hidden />
                {t("dashboard.newSale")}
              </Button>
            </Link>
          )}
          {can("purchase.manage") && (
            <Link to="/t/$tenantId/purchases/new" params={{ tenantId }}>
              <Button variant="outline">
                <Truck aria-hidden />
                {t("dashboard.newPurchase")}
              </Button>
            </Link>
          )}
          {can("item.manage") && (
            <Link to="/t/$tenantId/products" params={{ tenantId }}>
              <Button variant="outline">
                <Package aria-hidden />
                {t("dashboard.addProduct")}
              </Button>
            </Link>
          )}
        </div>
      </div>

      <ErrorMessage error={stats.error} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {d?.salesTotal !== null && d?.salesTotal !== undefined && (
          <Stat
            label={t("dashboard.salesToday")}
            value={money(d.salesTotal)}
            hint={t("dashboard.salesCount", { count: d.salesCount })}
          />
        )}
        {d?.grossProfit !== null && d?.grossProfit !== undefined && (
          <Stat label={t("dashboard.grossProfit")} value={money(d.grossProfit)} />
        )}
        {d?.cashInHand !== null && d?.cashInHand !== undefined && (
          <Stat label={t("dashboard.cashInHand")} value={money(d.cashInHand)} />
        )}
        {d?.receivable !== null && d?.receivable !== undefined && (
          <Stat label={t("dashboard.receivable")} value={money(d.receivable)} />
        )}
        {d?.payable !== null && d?.payable !== undefined && (
          <Stat label={t("dashboard.payable")} value={money(d.payable)} />
        )}
        {d && <Stat label={t("dashboard.lowStock")} value={num(d.lowStockCount)} />}
        {d?.nearExpiryCount !== null && d?.nearExpiryCount !== undefined && (
          <Stat
            label={t("dashboard.nearExpiry")}
            value={num(d.nearExpiryCount)}
            hint={
              d.expiredLotCount ? (
                <span className="font-medium text-destructive">
                  {t("dashboard.expired", { count: d.expiredLotCount })}
                </span>
              ) : undefined
            }
          />
        )}
      </div>

      <WeeklySummaryCard />

      {d && d.trend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("dashboard.trend")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Trend data={d.trend} />
          </CardContent>
        </Card>
      )}

      {!dismissed && can("tenant.settings.manage") && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle>{t("dashboard.setupTitle")}</CardTitle>
              <CardDescription>
                {t("dashboard.setupProgress", { done: completedSteps.length, total: STEPS.length })}
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => update.mutate({ dismissed: true })}
              disabled={update.isPending}
            >
              {t("dashboard.dismiss")}
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div
              className="h-2 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={STEPS.length}
              aria-valuenow={completedSteps.length}
            >
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${(completedSteps.length / STEPS.length) * 100}%` }}
              />
            </div>
            <ul className="divide-y">
              {STEPS.map((step) => {
                const done = completedSteps.includes(step);
                return (
                  <li key={step} className="flex items-center gap-3 py-3">
                    {done ? (
                      <CheckCircle2 className="size-5 shrink-0 text-success" aria-hidden />
                    ) : (
                      <Circle className="size-5 shrink-0 text-muted-foreground" aria-hidden />
                    )}
                    <span
                      className={
                        done
                          ? "flex-1 text-sm text-muted-foreground line-through"
                          : "flex-1 text-sm"
                      }
                    >
                      {t(`dashboard.steps.${step}` as MessageKey)}
                    </span>
                    {!done && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => update.mutate({ complete: [step] })}
                        disabled={update.isPending}
                      >
                        {t("dashboard.markDone")}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
            <ErrorMessage error={update.error} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
